import React, { useContext } from "react"
import { DeepReadonly } from "ts-essentials"
import { DashboardContext } from "../context/dashboard"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useExtractDashboardContextProperties } from "./extract-context-properties"

interface DashboardAnalyticsProviderProps {
  children: React.ReactNode
}

/**
 * Provides dashboard analytics context with automatic property extraction
 * and "Dashboard" prefix for all events.
 *
 * This provider wraps children with analytics context that includes:
 * - Dashboard-specific context properties (modelId, instrumentId, etc.)
 * - "Dashboard" prefix for all tracked events
 * - Inheritance from parent analytics contexts if nested
 *
 */
export function DashboardAnalyticsProvider({
  children,
}: DeepReadonly<DashboardAnalyticsProviderProps>) {
  const dashboardContext = useContext(DashboardContext)

  // Hook handles memoization internally with primitive dependencies
  const contextProperties = useExtractDashboardContextProperties(dashboardContext)

  const analyticsContextValue = useAnalyticsWithProperties(contextProperties, "Dashboard")

  return (
    <AnalyticsContext.Provider value={analyticsContextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export default DashboardAnalyticsProvider
